import {
    WorkflowUpdateWorkflowRequest,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import createWorkflowApi from '@rhg/libs/api/create-workflow-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { invalidateQueryKey, useMutation } from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';
import {
    workflowByDefnIdForRecipientQueryOptions,
    workflowByTypeForRecipientQueryOptions,
} from '../use-workflows';

interface UpdateWorkflowParameters {
    workflowDefnId?: string;
    workflowType?: WorkflowWorkflowType;
    request: WorkflowUpdateWorkflowRequest;
}
export function useUpdateWorkflow() {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle } = useActiveCircle();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            workflowDefnId = '',
            request,
        }: UpdateWorkflowParameters) => {
            const api = createWorkflowApi(getAccessTokenSilently);
            const response = await api.v1UpdateWorkflow({
                circleAuthorizationToken: activeCircle!.authorizationJWT!,
                recipientId: activeCircle!.recipient.id,
                workflowDefnId,
                workflowUpdateWorkflowRequest: request,
            });
            return response;
        },
        onSuccess: async (_, { workflowDefnId = '', workflowType }) => {
            // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
            await Promise.all([
                invalidateQueryKey(
                    workflowByTypeForRecipientQueryOptions.queryKey({
                        workflowType,
                        recipientId: activeCircle!.recipient.id,
                    })
                ),
                invalidateQueryKey(
                    workflowByDefnIdForRecipientQueryOptions.queryKey({
                        workflowDefnId,
                        recipientId: activeCircle!.recipient.id,
                    })
                ),
            ]);
        },
    });
    return {
        updateWorkflow: mutate,
        updateWorkflowAsync: mutateAsync,
        ...otherProps,
    };
}

interface LeaveWorkflowParameters {
    workflowDefnId?: string;
    workflowType?: WorkflowWorkflowType;
}
export function useLeaveWorkflow() {
    const { updateWorkflowAsync } = useUpdateWorkflow();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            workflowDefnId,
            workflowType,
        }: LeaveWorkflowParameters) =>
            updateWorkflowAsync({
                workflowDefnId,
                workflowType,
                request: { state: 'wrkf_abandoned' },
            }),
    });

    return {
        leaveWorkflow: mutate,
        leaveWorkflowAsync: mutateAsync,
        ...otherProps,
    };
}
